/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Ecc } from "./Ecc";
import { QrCode } from "./QrCodeLib";

/**
 * Result object containing the generated QR code image data.
 */
interface QrCodeImageResult {
  /**
   * Base64-encoded PNG image data URL that can be used directly in img src attributes.
   * Format: "data:image/png;base64,..."
   */
  bitmap: string;
  /**
   * The size of the QR code grid including the border (in modules, not pixels).
   */
  size: number;
}

/**
 * Utility class for generating QR code images.
 */
export class QrCodeGenerator {
  /**
   * Generates a QR code image as a bitmap from the provided data.
   *
   * @param data - The text data to encode in the QR code
   * @param scale - The scaling factor for each QR module (higher values create larger images)
   * @param borderSize - The size of the white border around the QR code (in modules, minimum 1)
   * @returns An object containing the bitmap data URL and the size of the QR code grid
   * @throws {Error} If canvas context cannot be created
   *
   * @example
   * ```typescript
   * const result = QrCodeGenerator.generateQrCode("https://example.com", 4, 2);
   * // Use in React/JSX:
   * <img src={result.bitmap} alt="QR Code" />
   * ```
   */
  public static generateQrCode(
    data: string,
    scale: number,
    borderSize: number
  ): QrCodeImageResult {
    // Generate the QR code using the input string
    const qr = QrCode.encodeText(data, Ecc.MEDIUM);

    // Get the size of the QR code grid
    const { size } = qr;

    // Ensure border size is at least 1
    const adjustedBorderSize = borderSize < 1 ? 1 : borderSize;

    // Calculate the new size with the border
    const newSize = size + 2 * adjustedBorderSize;
    const width = newSize * scale;
    const height = width;

    // Create canvas for bitmap generation
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      throw new Error("Failed to get canvas context");
    }

    // Create ImageData for pixel manipulation
    const imageData = ctx.createImageData(width, height);
    const pixels = imageData.data;
    let pixelIndex = 0;

    for (let row = 0; row < newSize; row += 1) {
      for (let expandRow = 0; expandRow < scale; expandRow += 1) {
        for (let col = 0; col < newSize; col += 1) {
          // Determine if the current module is part of the border
          const isBorder =
            row < adjustedBorderSize ||
            row >= size + adjustedBorderSize ||
            col < adjustedBorderSize ||
            col >= size + adjustedBorderSize;
          let gray = 0xff; // Default to white for the border

          if (!isBorder) {
            // Adjust the coordinates to account for the border
            const qrRow = row - adjustedBorderSize;
            const qrCol = col - adjustedBorderSize;
            gray = qr.getModule(qrCol, qrRow) ? 0 : 0xff;
          }

          for (let expandCol = 0; expandCol < scale; expandCol += 1) {
            pixelIndex += 1;
            pixels[pixelIndex] = gray; // Red
            pixelIndex += 1;
            pixels[pixelIndex] = gray; // Green
            pixelIndex += 1;
            pixels[pixelIndex] = gray; // Blue
            pixelIndex += 1;
            pixels[pixelIndex] = 0xff; // Alpha
          }
        }
      }
    }

    // Put the image data on canvas
    ctx.putImageData(imageData, 0, 0);

    // Convert canvas to bitmap data URL
    const bitmap = canvas.toDataURL("image/png");

    // Return the result object
    return {
      bitmap,
      size: newSize
    };
  }
}
